import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import { Button, Flex, FormControl, FormLabel } from "@chakra-ui/react";
import { ConfirmDialog, SelectPartner } from "@components";
import { employerRequiredMsg, formatNickname } from "@utils";
import { EmployerFormSub } from "components/employerForm";
import { AdminContext } from "context";
import { EActionType, EEmployers, EToastStatus } from "enums";
import { useFormik } from "formik";
import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { addNewEmployer, deleteEmployer, updateEmployer } from "services";
import { IEmployer } from "types";
import * as Yup from "yup";

interface AddPartnerModalProps {
  showModal: boolean;
  onDecline: () => void;
  action: () => void;
  data?: IEmployer;
}

const validationSchema = Yup.object().shape({
  employerName: Yup.string().required(({ path }) => employerRequiredMsg(path)),
  TPAName: Yup.string().required(({ path }) => employerRequiredMsg(path)),
  partnerId: Yup.string().required(({ path }) => employerRequiredMsg(path)),
  employerNickname: Yup.string().required(({ path }) =>
    employerRequiredMsg(path)
  ),
});

export const EmployerModal = ({
  showModal,
  onDecline,
  data,
  action,
}: AddPartnerModalProps) => {
  const cancelRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useContext(AdminContext);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const formik = useFormik({
    initialValues: {
      employerName: data?.employerName,
      employerNickname: data?.employerNickname,
      TPAName: data?.TPAName,
      partnerId: data?.partnerId,
    },
    validationSchema,
    onSubmit: async () => {
      handleAddNewEmployer();
    },
  });
  const {
    values,
    setFieldValue,
    resetForm,
    isValid,
    dirty,
    errors,
    submitForm,
  } = formik;

  const handleAddNewEmployer = async () => {
    try {
      setIsSubmitting(true);
      if (data) {
        await updateEmployer({ ...values, _id: data._id });
        showToast("Success", "Update Employer success!", EToastStatus.SUCCESS);
      } else {
        await addNewEmployer(values);
        showToast("Success", "Create Employer success!", EToastStatus.SUCCESS);
      }
      action();
      onDecline();
      resetForm();
    } catch (error: any) {
      showToast("Error", error?.response?.data?.message, EToastStatus.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    resetForm({
      values: {
        employerName: data?.employerName,
        employerNickname: data?.employerNickname,
        TPAName: data?.TPAName,
        partnerId: data?.partnerId,
      },
    });
  }, [data]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value;
    const type = e?.target?.name;
    if (!type) return;
    if (type === EEmployers.EmployerNickname) {
      return setFieldValue(type, formatNickname(value));
    }
    setFieldValue(type, value);
  };

  const handlePartnerChange = (e: { value: string; label: string }) => {
    const value = e.value;
    formik.setFieldValue(EEmployers.PartnerId, value);
  };

  const handleDeleteEmployer = async () => {
    try {
      await deleteEmployer(data._id);
      showToast(
        "Success",
        "Delete Employer successfully!",
        EToastStatus.SUCCESS
      );
      action();
      onDecline();
      setOpenConfirmDialog(false);
    } catch (error: any) {
      showToast("Error", error?.response?.data?.message, EToastStatus.ERROR);
    } finally {
      setIsSubmitting(false);
      setOpenConfirmDialog(false);
    }
  };

  const handleClose = () => {
    onDecline();
    resetForm();
  };

  const isDisabled = useMemo(() => {
    return !dirty || !isValid;
  }, [dirty, isValid]);

  return (
    <Modal
      finalFocusRef={cancelRef}
      onClose={handleClose}
      isOpen={showModal}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{data ? "Edit Employer" : "Add Employer"} </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex gap={6} flexDirection="column">
            <Flex gap={6}>
              <EmployerFormSub.Input
                name={EEmployers.EmployerName}
                value={values?.employerName}
                onChange={handleInputChange}
                error={errors?.employerName}
              />
            </Flex>
            <EmployerFormSub.Input
              name={EEmployers.EmployerNickname}
              value={values?.employerNickname}
              onChange={handleInputChange}
              error={errors?.employerNickname}
            />
            <Flex gap={6}>
              <EmployerFormSub.Input
                name={EEmployers.TPAName}
                value={values?.TPAName}
                onChange={handleInputChange}
                error={errors?.TPAName}
              />
            </Flex>
            <Flex gap={6}>
              <FormControl>
                <FormLabel display="flex" alignItems="center" gap={2}>
                  Partner
                </FormLabel>
                <SelectPartner
                  handlePartnerChange={handlePartnerChange}
                  data={data}
                  error={errors.partnerId}
                />
              </FormControl>
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter gap={5}>
          {data && (
            <Button
              isLoading={false}
              colorScheme="red"
              onClick={() => setOpenConfirmDialog(true)}
            >
              Delete
            </Button>
          )}
          <Button
            isLoading={isSubmitting}
            isDisabled={isDisabled}
            colorScheme="purple"
            onClick={submitForm}
          >
            {data ? "Save" : "Add "}
          </Button>
        </ModalFooter>
      </ModalContent>
      <ConfirmDialog
        showModal={openConfirmDialog}
        onAccept={handleDeleteEmployer}
        onDecline={() => setOpenConfirmDialog(false)}
        title={EActionType.DELETE}
        content={<>Do you want to {EActionType.DELETE} this Employer?</>}
      />
    </Modal>
  );
};
